/**
 * Guided setup — step model and DB-derived progression.
 *
 * Progress is no longer persisted: the furthest step a merchant can reach is derived from what
 * exists server-side (store → products → shipping → payment → checkout). The URL holds the cursor
 * within that range; {@link clampStep} stops it running ahead of what the DB actually supports.
 *
 * The flow's shape depends on the store kind. Digital stores have nothing to ship, so `shipping` is
 * removed from the flow entirely (not just hidden) — they go products → payment → checkout.
 */

import { SETUP_MACRO_STEPS, type SetupMacroStepDefinition } from '@/components/setup/experience/constants';

/** Linear wizard steps, in order. `complete` is a terminal success screen, reached by navigation. */
export type SetupStep = 'store' | 'products' | 'shipping' | 'payment' | 'checkout' | 'complete';

/** Stage rendered by the shell — the steps plus the destructive recovery fallback. */
export type SetupExperienceStage = SetupStep | 'recover';

/**
 * Canonical (physical) step order and source of truth for the flow. The digital flow is derived
 * from this by dropping `shipping`; the type union and every step list derive from it too.
 */
export const SETUP_LINEAR_STEPS = ['store', 'products', 'shipping', 'payment', 'checkout'] as const;

export type SetupLinearStep = (typeof SETUP_LINEAR_STEPS)[number];

export interface SetupMilestones {
  hasStore: boolean;
  hasProducts: boolean;
  hasIntegration: boolean;
  hasShipping: boolean;
  hasPayment: boolean;
  hasFunnel: boolean;
}

/**
 * The ordered linear steps for the store kind. Digital stores skip `shipping` outright — there is
 * nothing to ship, so it is absent from the flow, not merely greyed out.
 */
export function getSetupLinearSteps(isDigital: boolean): SetupLinearStep[] {
  return SETUP_LINEAR_STEPS.filter((step) => !(isDigital && step === 'shipping'));
}

/**
 * Steps shown in the progress strip. `store` is a full-screen first screen (it decides the shape of
 * everything after), so it never appears in the strip: the strip starts at `products` and holds 4
 * entries for physical stores, 3 for digital.
 */
export function getSetupStripSteps(isDigital: boolean): SetupLinearStep[] {
  return getSetupLinearSteps(isDigital).filter((step) => step !== 'store');
}

/** Strip definitions (id + i18n label) for the store kind — the single source the strip renders. */
export function getSetupStripDefinitions(isDigital: boolean): SetupMacroStepDefinition[] {
  const stripSteps = getSetupStripSteps(isDigital);
  return SETUP_MACRO_STEPS.filter((step) => stripSteps.includes(step.id as SetupLinearStep));
}

function isLinearStep(step: string, isDigital: boolean): step is SetupLinearStep {
  return (getSetupLinearSteps(isDigital) as readonly string[]).includes(step);
}

/**
 * Fixed canonical funnel position for analytics (`store` = 0 … `complete` = 5), derived from
 * {@link SETUP_LINEAR_STEPS} — NOT the store-kind-filtered strip index. Digital stores skip
 * `shipping`, so a relative index would report `payment` as 3 for physical merchants and 2 for
 * digital ones, making the two populations incomparable in the analytics funnel. A digital store
 * must simply never emit index 2.
 */
export function getSetupStepIndex(step: SetupStep): number {
  if (step === 'complete') return SETUP_LINEAR_STEPS.length;
  return (SETUP_LINEAR_STEPS as readonly string[]).indexOf(step);
}

/**
 * Progress-strip index for a step. The strip excludes `store`, so `store` (and anything off the
 * strip) maps to 0; `complete` sits on the last strip entry, like `checkout`.
 */
export function stepToStripIndex(step: SetupStep, isDigital: boolean): number {
  const stripSteps = getSetupStripSteps(isDigital);
  if (step === 'complete') return stripSteps.length - 1;
  const index = stripSteps.indexOf(step as SetupLinearStep);
  return index < 0 ? 0 : index;
}

/**
 * The furthest step the merchant has genuinely unlocked, derived purely from DB milestones.
 * Tops out at `checkout` — `complete` is never auto-resumed (it's a deliberate end-of-flow screen).
 * Digital stores skip the shipping gate entirely.
 */
export function deriveFurthestStep(m: SetupMilestones, isDigital: boolean): SetupLinearStep {
  if (!m.hasStore) return 'store';
  if (!m.hasProducts && !m.hasIntegration) return 'products';
  if (!isDigital && !m.hasShipping) return 'shipping';
  if (!m.hasPayment) return 'payment';
  return 'checkout';
}

/**
 * Clamp the requested step to what the DB allows: never ahead of `furthest`. `complete` is only
 * reachable once a funnel exists; otherwise it falls back to `checkout`. A step that doesn't exist
 * in the current flow (e.g. `shipping` on a digital store) falls back to `furthest`.
 */
export function clampStep(
  requested: SetupStep | null,
  furthest: SetupLinearStep,
  hasFunnel: boolean,
  isDigital: boolean,
): SetupStep {
  if (requested === 'complete') return hasFunnel ? 'complete' : 'checkout';
  if (!requested || !isLinearStep(requested, isDigital)) return furthest;
  const steps = getSetupLinearSteps(isDigital);
  const requestedIndex = steps.indexOf(requested);
  const furthestIndex = steps.indexOf(furthest);
  return steps[Math.min(requestedIndex, furthestIndex)];
}
